import { useState, forwardRef } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";

/**
 * Password input with a toggle-visibility eye icon on the right.
 *
 * Behaviour:
 *   - Renders exactly like a normal <Input>, but with type="password" that
 *     flips to "text" while the user holds/toggles the eye. State is local
 *     to each instance (Login vs. Register vs. ChangePassword don't share).
 *   - Uses `forwardRef` so parent forms can still ref the underlying input
 *     (autofocus, autofill, react-hook-form).
 *   - `data-testid` on the OUTER wrapper is preserved on the input; the
 *     eye button gets `${data-testid}-toggle` for playwright.
 */
const PasswordInput = forwardRef(function PasswordInput(
  { className = "", "data-testid": testId, ...props },
  ref
) {
  const [visible, setVisible] = useState(false);
  const btnTestId = testId ? `${testId}-toggle` : undefined;

  return (
    <div className="relative">
      <Input
        ref={ref}
        {...props}
        type={visible ? "text" : "password"}
        data-testid={testId}
        // pr-11 to leave room for the eye button; keep the rest of the
        // caller's className so borders / heights match the parent form.
        className={`pr-11 ${className}`}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Hide password" : "Show password"}
        aria-pressed={visible}
        data-testid={btnTestId}
        tabIndex={-1}
        // Inherit the input's text color (currentColor) via `text-current`
        // so the icon reads on any background — dark hero cards, white
        // form cards, or a coloured card body all "just work" without
        // per-caller styling. Opacity is used for the resting/hover
        // states instead of hard-coded slate shades.
        className="absolute inset-y-0 right-0 flex items-center justify-center w-10 text-current opacity-50 hover:opacity-100 transition-opacity"
      >
        {visible ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
});

export default PasswordInput;
